import styled from "styled-components";
import { memo, useEffect, useState } from "react";
import moment from "moment";
import { ClipLoaderCommon } from "@components";
import {
    BookingPriceGroup,
    CheckProductAvailsDateRange,
    IBookingAvailability,
    SelectedAvailability,
} from "@interfaces";
import { breakpoint, sliceString, theme } from "@utilities";
import { AvailBlockModule } from "@module";
import { getCalendarDays, getMonthsInRange } from "src/Utilities/cartHelper";
import { BOOKING_DATE_FORMAT } from "@constants";
import { AvailabilityRangePicker, IAvailabilityRangePickerProps } from "./AvailabilityRangePicker";

import { DateField } from "@mui/x-date-pickers/DateField";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

interface ICheckAvailTableProps {
    availabilities: IBookingAvailability[];
    selectedAvailabilities: SelectedAvailability[];
    setSelectedAvailabilities: (newAvailabilities: SelectedAvailability[]) => void;
    isLoading: boolean;
    range: CheckProductAvailsDateRange;
    productPriceGroup: BookingPriceGroup;
    // Cache-related props for availability caching when user selects a date
    productId?: string;
    apiProviderId?: number;
    groupFaresForAvailabilityCheck?: boolean;
    timeId?: string;
}

interface IHeaderTableProps extends IAvailabilityRangePickerProps {
    fareName: string;
}

export const HeaderTableComponent = memo((props: IHeaderTableProps) => {
    return (
        <Header>
            <div>
                <p className="title">Fare Type</p>
                <p className="name">{sliceString(props?.fareName || "", 10)}</p>
            </div>
            <div className="date">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DateField>
                        <AvailabilityRangePicker key="table-header" {...props} />
                    </DateField>
                </LocalizationProvider>
            </div>
        </Header>
    );
});

export const CheckAvailTableModule = memo((props: ICheckAvailTableProps) => {
    const {
        availabilities,
        isLoading,
        range,
        selectedAvailabilities,
        setSelectedAvailabilities,
        productPriceGroup,
        productId,
        apiProviderId,
        groupFaresForAvailabilityCheck,
        timeId,
    } = props;

    const isExpanded = availabilities.length > 0;
    const [monthsInRange, setMonthsInRange] = useState(
        getMonthsInRange(
            range.startDate,
            moment(range.startDate).add(range.numDays, "days").toDate(),
        ),
    );
    const [activeMonthIndex, setActiveMonthIndex] = useState(0);
    const [activeMonth, setActiveMonth] = useState(monthsInRange[activeMonthIndex]!);
    const [calendarDays, setCalendarDays] = useState(
        getCalendarDays(activeMonth.year(), activeMonth.month()),
    );

    // Check if the date range is within a single month
    const isSingleMonth = moment(range.startDate).isSame(
        moment(range.startDate).add(range.numDays - 1, "days"),
        "month",
    );

    // Combine both effects into one so that changes in date.from, date.to, or activeMonthIndex recalc the calendar
    useEffect(() => {
        const fromDate = range.startDate;
        const toDate = moment(range.startDate)
            .add(range.numDays - 1, "days")
            .toDate();
        const newMonths = getMonthsInRange(fromDate, toDate);
        setMonthsInRange(newMonths);

        // Reset activeMonthIndex if it's out of bounds for the new range
        let validIndex = activeMonthIndex;
        if (validIndex >= newMonths.length) {
            validIndex = Math.max(0, newMonths.length - 1);
            setActiveMonthIndex(validIndex);
        }

        const newActiveMonth = newMonths[validIndex] || newMonths[0];
        setActiveMonth(newActiveMonth);
        setCalendarDays(getCalendarDays(newActiveMonth.year(), newActiveMonth.month()));
    }, [range, activeMonthIndex]);

    const weekModel = [
        { id: 0, title: "Sun" },
        { id: 1, title: "Mon" },
        { id: 2, title: "Tue" },
        { id: 3, title: "Wed" },
        { id: 4, title: "Thu" },
        { id: 5, title: "Fri" },
        { id: 6, title: "Sat" },
    ];

    const handleNextPage = () => {
        if (activeMonthIndex < monthsInRange.length - 1) {
            setActiveMonthIndex(activeMonthIndex + 1);
        }
    };

    const handlePrevPage = () => {
        if (activeMonthIndex > 0) {
            setActiveMonthIndex(activeMonthIndex - 1);
        }
    };

    return (
        <Container isExpanded={isExpanded}>
            {/* <HeaderTableComponent
                fareName={props.fareName}
                limitDate={limitDate}
                dateRange={date}
                onRangeChange={onRangeChange}
            /> */}
            <Main isExpanded={isExpanded} dataPagination={availabilities.length > 0}>
                {isLoading ? (
                    <ClipLoaderCommon color={theme.colors.highlight} size={70} margin={0} />
                ) : availabilities.length > 0 ? (
                    <>
                        <div className="listNavBtn">
                            {!isSingleMonth && (
                                <button
                                    className="navigateBtn prevBtn"
                                    onClick={handlePrevPage}
                                    disabled={activeMonthIndex === 0}
                                >
                                    <img src="/images/fi_chevron-left.png" alt="" />
                                </button>
                            )}
                            <div className={`month ${isSingleMonth ? "month-full" : ""}`}>
                                {activeMonth.format("MMMM")}
                            </div>
                            {!isSingleMonth && (
                                <button
                                    className="navigateBtn nextBtn"
                                    onClick={handleNextPage}
                                    disabled={activeMonthIndex >= monthsInRange.length - 1}
                                >
                                    <img src="/images/fi_chevron-left.png" alt="" />
                                </button>
                            )}
                        </div>
                        <div className="header _row">
                            {weekModel.map((item) => (
                                <div className="headerItem col_1_7" key={item.id}>
                                    {item.title}
                                </div>
                            ))}
                        </div>
                        <div className="listBlock _row">
                            {calendarDays.map((calendarDay) => {
                                const availIndex = availabilities.findIndex((avail) =>
                                    moment(avail.BookingDate, BOOKING_DATE_FORMAT).isSame(
                                        calendarDay,
                                    ),
                                );
                                const availData =
                                    availIndex < 0 ? null : availabilities[availIndex];
                                const isChecked =
                                    selectedAvailabilities.filter(
                                        (avail) => avail.availIndex === availIndex,
                                    ).length > 0;
                                const dayDate = calendarDay.toDate();
                                return (
                                    <div
                                        className="col_1_7"
                                        key={`${dayDate.getDate()}_${dayDate.getMonth()}`}
                                    >
                                        <AvailBlockModule
                                            date={dayDate}
                                            availIndex={availIndex}
                                            availability={availData}
                                            isChecked={isChecked}
                                            productPriceGroup={productPriceGroup}
                                            // Pass cache params to trigger API call when date is selected
                                            productId={productId}
                                            apiProviderId={apiProviderId?.toString()}
                                            groupFaresForAvailabilityCheck={
                                                groupFaresForAvailabilityCheck
                                            }
                                            timeId={timeId}
                                            onCheck={(avail) => {
                                                const exists = selectedAvailabilities.some(
                                                    (dateItem) =>
                                                        dateItem.BookingDate === avail.BookingDate,
                                                );
                                                if (exists) {
                                                    const updated = selectedAvailabilities.filter(
                                                        (item): boolean =>
                                                            item.BookingDate !== avail.BookingDate,
                                                    );
                                                    setSelectedAvailabilities(updated);
                                                } else {
                                                    setSelectedAvailabilities([
                                                        ...selectedAvailabilities,
                                                        avail,
                                                    ]);
                                                }
                                            }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </>
                ) : (
                    <p>Please choose different or shorter date range (max 30 days)</p>
                )}
            </Main>
        </Container>
    );
});

const Container = styled.div<{ isExpanded: boolean }>`
    margin: 0 auto;
    width: ${(p) => p.theme.widths.wide_700};
    min-height: ${(p) => (p.isExpanded ? "510px" : "unset !important")};
    display: flex;
    flex-direction: column;

    ${breakpoint.breakIpadPro`
        width: ${(p) => p.theme.widths.wide_900};
        min-height: 584px;
    `}

    ${breakpoint.breakLarge`
        width: ${(p) => p.theme.widths.wide_1110};
        min-height: 774px;
    `}
`;

const Header = styled.div`
    position: sticky;
    top: 0;
    z-index: 1;
    color: #fff;
    background-color: ${(p) => p.theme.colors.black};
    display: flex;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    justify-content: space-between;
    align-items: center;
    padding: 14px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    ${breakpoint.breakLarge`
        padding: 28px 26px 21px 26px;
    `}

    .col_2 {
        flex: 2;
    }

    .col_3 {
        flex: 3;
    }

    .date {
        display: flex;

        .datePicker {
            margin: 0 10px;

            .MuiInputLabel-animated {
                color: #fff;
            }

            .MuiFormControl-marginNormal {
                margin: 0;
            }

            .MuiInputBase-root {
                background-color: ${(p) => p.theme.colors.gray_1} !important;
                height: 36px;
                width: 135px;

                ${breakpoint.breakIpadPro`
                    width: 160px;
                `}

                ${breakpoint.breakLarge`
                    width: 214px;
                    height: 54px;
                `}
            }

            .MuiInputBase-input {
                color: #fff;
            }
        }
    }

    .checkAvailBtn {
        display: flex;
        align-items: flex-end;
        margin-left: 5px;

        ${breakpoint.breakLarge`
            margin-left: 50px;
        `}
    }
`;

const Main = styled.div<{ isExpanded: boolean; dataPagination?: boolean }>`
    min-height: 60vh;
    background-color: #fff;
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
    padding: 13px 9px 0 9px;
    flex: ${(p) => (p.isExpanded ? "1" : "unset")};
    position: relative;
    display: flex;
    flex-direction: column;
    margin-bottom: 48px;

    ${breakpoint.breakTablet`
        padding: 35px 30px 48px 30px;
    `}

    @media (min-width: 768px) {
        padding-bottom: ${(p) => (p.dataPagination ? "48px" : "100px")};
    }

    ._row {
        display: flex;
        flex-wrap: wrap;
    }

    .col_1_7 {
        width: 14.2857%;
        font-size: 16px;
        font-weight: 400;
        line-height: 21px;
    }

    .header {
        width: 100%;

        .headerItem {
            padding-right: 9px;
            text-align: right;
            color: ${(p) => p.theme.colors.gray_3};
            margin-bottom: 5px;
            font-size: 12px;

            ${breakpoint.breakLarge`
                margin-bottom: 9px;
                font-size: 16px;
            `}
        }
    }

    .listBlock {
        width: 100%;
        flex: 1;
    }

    .listBtn {
        display: flex;
        justify-content: flex-end;
    }

    .listNavBtn {
        display: flex;
        justify-content: flex-end;
        padding-bottom: 10px;
    }

    .month {
        width: 150px;
        display: flex;
        justify-content: center;
        align-items: center;
        border: 1px solid ${(p) => p.theme.colors.gray_3};
        color: ${(p) => p.theme.colors.highlight};
        font-weight: bold;

        &.month-full {
            width: 150px;
            height: 40px;
            border-radius: 5px;
        }
    }

    .navigateBtn {
        background-color: ${(p) => p.theme.colors.gray_2};
        width: 30px;
        height: 30px;
        padding-top: 4px;
        border-radius: 0px;
        ${breakpoint.breakIpadPro`
            width: 40px;
            height: 40px;
        `}

        &.nextBtn {
            img {
                transform: rotate(180deg);
            }
        }

        &:disabled {
            background-color: ${(p) => p.theme.colors.gray_3};
        }
    }
`;
